import { useState, useEffect } from 'react';
import { Input } from './Input';
import { Button } from './Button';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { getStatuses, createLead, updateLead, checkDuplicates, getEvents, getMessageTypes, getActivities, getGroups } from '../services/api';

export const LeadForm = ({ lead = null, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    message: '',
    campaign_name: '',
    status_id: '',
    action_date: '',
    event_id: '',
    group_id: '',
    preferred_days: [],
    message_types: [],
  });
  const [statuses, setStatuses] = useState([]);
  const [events, setEvents] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    fetchStatuses();
    fetchEvents();
    fetchMessageTypes();
    fetchActivities();
    fetchGroups();

    if (lead) {
      // Editing existing lead
      // Format action_date to datetime-local input format if it exists (preserve local timezone)
      let actionDateValue = '';
      if (lead.action_date) {
        const date = new Date(lead.action_date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        actionDateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setFormData({
        full_name: lead.field_data?.full_name || '',
        phone: lead.field_data?.phone || '',
        message: lead.field_data?.message || '',
        campaign_name: lead.field_data?.campaign_name || '',
        status_id: lead.status_id || '',
        action_date: actionDateValue,
        event_id: lead.event_id || '',
        group_id: lead.group_id || '',
        preferred_days: lead.field_data?.preferred_days || [],
        message_types: (lead.field_data?.message_types || []).map(id => parseInt(id, 10)),
      });
    } else {
      // Creating new lead - start with blank action_date
      setFormData(prev => ({
        ...prev,
        action_date: '',
      }));
    }
  }, [lead]);

  const fetchStatuses = async () => {
    try {
      const data = await getStatuses();
      setStatuses(data);

      // Set default status to first one if creating new lead
      if (!lead && data.length > 0) {
        setFormData(prev => ({ ...prev, status_id: data[0].id }));
      }
    } catch (error) {
      toast.error(t('failedToLoadStatuses'));
    }
  };

  const fetchEvents = async () => {
    try {
      // Fetch only active, upcoming events
      const data = await getEvents({ is_active: 'true', upcoming_only: 'true' });
      setEvents(data);
    } catch (error) {
      toast.error(t('failedToLoadEvents') || 'Failed to load events');
    }
  };

  const fetchMessageTypes = async () => {
    try {
      const types = await getMessageTypes();
      setMessageTypes(types);
    } catch (error) {
      console.error('Failed to load message types:', error);
      toast.error(t('failedToLoadMessageTypes') || 'Failed to load message types');
    }
  };

  const fetchActivities = async () => {
    try {
      const data = await getActivities(true); // Only active activities
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities') || 'Failed to load activities');
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroups(true); // Only active groups
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error(t('failedToLoadGroups') || 'Failed to load groups');
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Normalize phone number: remove spaces, dashes, and replace +972 with 0
    let normalizedValue = value;
    if (name === 'phone') {
      normalizedValue = value
        .replace(/[\s-]/g, '') // Remove spaces and dashes
        .replace(/^\+972/, '0'); // Replace +972 at the start with 0
    }

    // Special handling for event selection - auto-fill action_date
    if (name === 'event_id' && normalizedValue) {
      const selectedEvent = events.find(event => event.id === normalizedValue);
      if (selectedEvent) {
        // Convert event_date to datetime-local format (keep original timezone)
        const eventDate = new Date(selectedEvent.event_date);
        const year = eventDate.getFullYear();
        const month = String(eventDate.getMonth() + 1).padStart(2, '0');
        const day = String(eventDate.getDate()).padStart(2, '0');
        const hours = String(eventDate.getHours()).padStart(2, '0');
        const minutes = String(eventDate.getMinutes()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

        setFormData(prev => ({
          ...prev,
          [name]: normalizedValue,
          action_date: formattedDate
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: normalizedValue }));
      }
    } else if (name === 'event_id' && !normalizedValue) {
      // Clear event selection
      setFormData(prev => ({ ...prev, [name]: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: normalizedValue }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Check for duplicates when phone number changes
    if (name === 'phone' && normalizedValue.trim().length >= 7) {
      await checkForDuplicates(normalizedValue.trim());
    } else if (name === 'phone' && normalizedValue.trim().length < 7) {
      setDuplicates([]);
    }
  };

  const handlePreferredDayToggle = (day) => {
    setFormData(prev => {
      const currentDays = prev.preferred_days || [];
      const newDays = currentDays.includes(day)
        ? currentDays.filter(d => d !== day)
        : [...currentDays, day];
      return { ...prev, preferred_days: newDays };
    });
  };

  const handleMessageTypeToggle = (typeId) => {
    setFormData(prev => {
      const currentTypes = prev.message_types || [];
      const newTypes = currentTypes.includes(typeId)
        ? currentTypes.filter(id => id !== typeId)
        : [...currentTypes, typeId];
      return { ...prev, message_types: newTypes };
    });
  };

  const checkForDuplicates = async (phone) => {
    try {
      setCheckingDuplicates(true);
      const result = await checkDuplicates(phone);

      // Filter out the current lead if editing
      const filteredDuplicates = result.duplicates.filter(dup => dup.id !== lead?.id);
      setDuplicates(filteredDuplicates);
    } catch (error) {
      // Silently fail - duplicates check is not critical
      setDuplicates([]);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t('fullNameRequired');
    }

    if (!formData.status_id) {
      newErrors.status_id = t('statusRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const leadData = {
        field_data: {
          full_name: formData.full_name,
          phone: formData.phone,
          campaign_name: formData.campaign_name,
          message: formData.message,
          preferred_days: formData.preferred_days,
          message_types: formData.message_types.map(id => id.toString()),
        },
        status_id: formData.status_id,
        source: 'manual', // Manual entry via UI
      };

      // Add action_date (null if empty, to allow clearing)
      if (formData.action_date) {
        leadData.action_date = new Date(formData.action_date).toISOString();
      } else if (lead && lead.action_date) {
        // When editing, explicitly set to null if empty to clear it
        leadData.action_date = null;
      }

      // Add event_id if provided (only include if it has a value)
      // Don't send event_id: null when editing unless explicitly cleared
      if (formData.event_id) {
        leadData.event_id = formData.event_id;
      } else if (lead && lead.event_id) {
        // Only clear event if it previously had one and user cleared it
        leadData.event_id = null;
      }

      // Add group_id if provided (only include if it has a value)
      // Don't send group_id: null when editing - group removal is done via Groups Manager
      if (formData.group_id) {
        leadData.group_id = parseInt(formData.group_id, 10);
      }

      if (lead) {
        // Update existing lead
        await updateLead(lead.id, leadData);
        toast.success(t('leadUpdated'));
      } else {
        // Create new lead
        await createLead(leadData);
        toast.success(t('leadCreated'));
      }

      onSuccess();
    } catch (error) {
      toast.error(lead ? t('failedToUpdateLead') : t('failedToCreateLead'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label={t('fullName')}
        name="full_name"
        value={formData.full_name}
        onChange={handleChange}
        error={errors.full_name}
        placeholder={t('placeholderFullName') || 'John Doe'}
        required
      />

      <div>
        <Input
          label={t('phone')}
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder={t('placeholderPhone') || '+1234567890'}
        />

        {/* Duplicate Warning */}
        {checkingDuplicates && (
          <div className="mt-2 text-sm text-gray-500">
            {t('checkingDuplicates') || 'Checking for duplicates...'}
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  {t('duplicateLeadsFound') || 'Possible duplicate leads found'}
                </p>
                <div className="mt-2 space-y-1">
                  {duplicates.map((dup) => (
                    <div key={dup.id} className="text-xs text-yellow-700">
                      • {dup.full_name} - {dup.status_name} ({new Date(dup.created_at).toLocaleDateString()})
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('activity') || 'Activity/Campaign'}
        </label>
        <select
          name="campaign_name"
          value={formData.campaign_name}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('selectActivity') || 'Select Activity'}</option>
          {activities.map((activity) => (
            <option key={activity.id} value={activity.activity_name}>
              {activity.activity_name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('status')} <span className="text-red-500">*</span>
        </label>
        <select
          name="status_id"
          value={formData.status_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          required
        >
          <option value="">{t('selectStatus')}</option>
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.status_name}
            </option>
          ))}
        </select>
        {errors.status_id && (
          <p className="mt-1 text-sm text-red-600">{errors.status_id}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('group') || 'Group'}
        </label>
        <select
          name="group_id"
          value={formData.group_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noGroup') || 'No Group'}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.group_name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {t('groupHelp') || 'Assign this lead to a group for bulk event assignments'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('assignToEvent') || 'Assign to Event'}
        </label>
        <select
          name="event_id"
          value={formData.event_id}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">{t('noEvent') || 'No Event'}</option>
          {events.map((event) => {
            const assignedCount = parseInt(event.assigned_count) || 0;
            const capacity = event.capacity || 0;
            const isFull = capacity > 0 && assignedCount >= capacity;
            const eventDate = new Date(event.event_date).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });

            return (
              <option key={event.id} value={event.id} disabled={isFull}>
                {event.event_name} - {eventDate}
                {isFull ? ` (${t('full') || 'Full'})` : capacity > 0 ? ` (${assignedCount}/${capacity})` : ''}
              </option>
            );
          })}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {t('assignToEventHelp') || 'Assigning to an event will automatically set the action date'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('actionDate') || 'Action Date'}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            name="action_date"
            value={formData.action_date ? formData.action_date.split('T')[0] : ''}
            onChange={(e) => {
              const dateValue = e.target.value;
              const timeValue = formData.action_date ? formData.action_date.split('T')[1] : '00:00';
              handleChange({ target: { name: 'action_date', value: dateValue ? `${dateValue}T${timeValue}` : '' } });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!!formData.event_id}
          />
          <input
            type="time"
            name="action_time"
            value={formData.action_date ? formData.action_date.split('T')[1] || '' : ''}
            onChange={(e) => {
              const timeValue = e.target.value;
              const dateValue = formData.action_date ? formData.action_date.split('T')[0] : '';
              if (dateValue) {
                handleChange({ target: { name: 'action_date', value: `${dateValue}T${timeValue}` } });
              }
            }}
            step="1800"
            pattern="[0-9]{2}:[0-9]{2}"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={!!formData.event_id}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {formData.event_id
            ? (t('actionDateFromEvent') || 'Action date is set from the event')
            : (t('actionDateHelp') || 'Trial session date, follow-up date, or next contact date')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('preferredDays') || 'Preferred Days'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map((day) => (
            <label
              key={day}
              className="flex items-center gap-2 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={formData.preferred_days?.includes(day) || false}
                onChange={() => handlePreferredDayToggle(day)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t(day)}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('preferredDaysHelp') || 'Select the days that work best for the lead'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('messageTypes') || 'Message Types'}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {messageTypes.map((type) => (
            <label
              key={type.id}
              className="flex items-center gap-2 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={formData.message_types?.includes(type.id) || false}
                onChange={() => handleMessageTypeToggle(type.id)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{type.name}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('messageTypesHelp') || 'Select how the lead prefers to be contacted'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('message')}
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('additionalNotes')}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          className="flex-1"
        >
          {lead ? t('updateLead') : t('createLead')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="flex-1"
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
};
